var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a, _b, _c;
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { useIntl } from 'react-intl';
import { useState, useCallback } from 'react';
import Button from './Button';
import PreferencesTable from './PreferencesTable';
import Typography from './Typography';
import theme from '../consts/theme';
import useModalA11y from '../hooks/useModalA11y';
import { srOnly } from '../styles/a11y';
import defaultPreferencesDialogTemplate from '../consts/default-preferences-template';
import saveCustomPreferences from '../utils/saveCustomPreferences';
var rootStyles = css({
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transitionDuration: '225ms',
    transitionProperty: 'opacity, visibility',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 10000,
});
var backdropStyles = css({
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 0,
});
var dialogStyles = css({
    backgroundColor: theme.colors.white,
    borderRadius: '0.25rem',
    boxShadow: "\n    0px 11px 15px -7px rgba(0, 0, 0, 0.2),\n    0px 24px 38px 3px rgba(0, 0, 0, 0.14),\n    0px 9px 46px 8px rgba(0, 0, 0, 0.12)\n  ",
    color: theme.colors.helsinkiBlueDark,
    margin: '2rem',
    maxHeight: 'calc(100% - 4rem)',
    maxWidth: 900,
    outline: 'none',
    overflowY: 'auto',
    position: 'relative',
    width: 'calc(100% - 4rem)',
    zIndex: 1,
});
var innerStyles = css((_a = {
        padding: '4rem 1.5rem',
        position: 'relative'
    },
    _a[theme.breakpoints.lg] = {
        padding: '6rem 10rem',
    },
    _a));
var closeButtonStyles = css({
    alignItems: 'center',
    appearance: 'none',
    backgroundColor: 'transparent',
    border: 0,
    borderRadius: 0,
    cursor: 'pointer',
    display: 'flex',
    height: 44,
    width: 44,
    padding: 0,
    position: 'absolute',
    top: '1.25rem',
    right: '1.25rem',
    justifyContent: 'center',
    zIndex: 1,
});
var titleStyles = css({
    marginBottom: '2rem',
});
var actionsWrapperStyles = css((_b = {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        marginTop: '2rem'
    },
    _b[theme.breakpoints.md] = {
        flexDirection: 'row',
    },
    _b));
var saveButtonStyles = css((_c = {},
    _c[theme.breakpoints.md] = {
        marginRight: 'auto',
    },
    _c));
var PreferencesDialog = function (_a) {
    var preferences = _a.preferences, visible = _a.visible, handleSave = _a.handleSave, onClose = _a.onClose;
    var titleId = 'consent-manager-title';
    var t = useIntl().formatMessage;
    var _b = useModalA11y({ visible: visible, onClose: onClose }), afterRef = _b.afterRef, beforeRef = _b.beforeRef, dialogRef = _b.dialogRef;
    var categories = defaultPreferencesDialogTemplate.categories;
    var initialPreferences = categories.reduce(function (acc, cur) {
        var _a;
        return (__assign(__assign({}, acc), (_a = {}, _a[cur.key] = !!preferences[cur.key], _a)));
    }, {});
    var _c = useState(initialPreferences), selectedPreferences = _c[0], setSelectedPreferences = _c[1];
    var _d = useState(false), preferencesHaveChanged = _d[0], setPreferencesHaveChanged = _d[1];
    var handleCategoryChange = useCallback(function (category, value) {
        if (!preferencesHaveChanged) {
            setPreferencesHaveChanged(true);
        }
        setSelectedPreferences(function (_selectedPreferences) {
            var _a;
            return (__assign(__assign({}, _selectedPreferences), (_a = {}, _a[category] = value, _a)));
        });
    }, [preferencesHaveChanged]);
    var handleSaveClick = useCallback(function () {
        handleSave(selectedPreferences);
    }, [selectedPreferences, handleSave]);
    var handleAcceptAllClick = useCallback(function () {
        handleSave(saveCustomPreferences(true));
    }, [handleSave]);
    var handleDenyAllClick = useCallback(function () {
        handleSave(saveCustomPreferences(false));
    }, [handleSave]);
    return (_jsxs("div", { "data-cy": "consent-manager-preferences", "aria-hidden": !visible, css: rootStyles, style: {
            opacity: visible ? 1 : 0,
            visibility: visible ? 'visible' : 'hidden',
        }, children: [_jsx("div", { css: backdropStyles, role: "presentation", onClick: onClose, tabIndex: -1 }), _jsx("div", { "data-before": "true", tabIndex: 0, "aria-hidden": "true", ref: beforeRef }), _jsxs("div", { "data-cy": "consent-manager-preferences-dialog", ref: dialogRef, css: dialogStyles, "aria-labelledby": titleId, role: "dialog", "aria-modal": "true", tabIndex: 0, children: [_jsxs("button", { css: closeButtonStyles, "data-cy": "modal-close", onClick: onClose, children: [_jsx("svg", { focusable: "false", "aria-hidden": "true", viewBox: "0 0 24 24", fill: theme.colors.helsinkiBlue, width: "24", height: "24", children: _jsx("path", { d: "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" }) }), _jsx("span", { css: srOnly, children: t({ id: 'consent_manager_preferences_close' }) })] }), _jsxs("div", { css: innerStyles, children: [_jsx(Typography, { id: titleId, css: titleStyles, El: "h2", variant: "h3", children: t({ id: 'banner_consent_to_cookies' }) }), _jsx(PreferencesTable, { onCategoryChange: handleCategoryChange, preferences: selectedPreferences }), _jsxs("div", { css: actionsWrapperStyles, children: [_jsx(Button, { "data-cy": "consent-manager-preferences-save", css: saveButtonStyles, variant: "reverse", disabled: !preferencesHaveChanged, onClick: handleSaveClick, children: t({ id: 'save' }) }), _jsx(Button, { "data-cy": "consent-manager-preferences-deny-all", variant: "basic", onClick: handleDenyAllClick, children: t({ id: 'banner_deny_all' }) }), _jsx(Button, { "data-cy": "consent-manager-preferences-allow-all", onClick: handleAcceptAllClick, children: t({ id: 'banner_allow_all' }) })] })] })] }), _jsx("div", { "data-after": "true", tabIndex: 0, "aria-hidden": "true", ref: afterRef })] }));
};
export default PreferencesDialog;
